import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { parkingSpots, statusColors, statusDescriptions } from './parkingData';
import { useAuth } from './useAuth';
import api from './api';
import {
  fetchAdsbExchangeAircraft,
  fetchAdsbFilters,
  fetchBackendAdsbAircraft,
} from './adsbExchange';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function siteCentersFromSpots(spots) {
  const sums = {};
  spots.forEach((spot) => {
    if (!spot.site || spot.lat == null || spot.lon == null) return;
    if (!sums[spot.site]) sums[spot.site] = { lat: 0, lon: 0, n: 0 };
    sums[spot.site].lat += Number(spot.lat);
    sums[spot.site].lon += Number(spot.lon);
    sums[spot.site].n += 1;
  });
  const centers = {};
  Object.entries(sums).forEach(([site, value]) => {
    centers[site] = [value.lat / value.n, value.lon / value.n];
  });
  return centers;
}

const SITE_CENTERS = siteCentersFromSpots(parkingSpots);

// Component to change map view when site changes
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}

const ADSBAircraftMap = ({ theme }) => {
  const [searchParams] = useSearchParams();
  const initialAircraftSearch = searchParams.get('aircraft') || searchParams.get('search') || '';
  const selectedSite = '';
  const [adsbAircraft, setAdsbAircraft] = useState([]);
  // Track aircraft-to-spot assignments: { hex_id: { spot: 'A25', timestamp: Date, aircraft: {...} } }
  const [spotAssignments, setSpotAssignments] = useState({});
  const [aircraftStatus, setAircraftStatus] = useState({}); // Map of buno -> status
  const [searchTerm, setSearchTerm] = useState(initialAircraftSearch);
  const [searchResult, setSearchResult] = useState(null); // Store found aircraft for centering
  const [adsbPollInterval, setAdsbPollInterval] = useState(2000);
  const [showOpenAip, setShowOpenAip] = useState(false);
  const [showNoaaRadar, setShowNoaaRadar] = useState(false);
  const [radarCacheBust, setRadarCacheBust] = useState(() => Date.now());
  const { isStaff, groups } = useAuth();
  const openAipApiKey = (process.env.REACT_APP_OPENAIP_API_KEY || '').trim();

  useEffect(() => {
    const aircraftSearch = searchParams.get('aircraft') || searchParams.get('search') || '';
    if (aircraftSearch) {
      setSearchTerm(aircraftSearch);
    }
  }, [searchParams]);

  // Refresh NOAA radar mosaic periodically while the overlay is enabled
  useEffect(() => {
    if (!showNoaaRadar) {
      return undefined;
    }
    setRadarCacheBust(Date.now());
    const intervalId = setInterval(() => {
      setRadarCacheBust(Date.now());
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [showNoaaRadar]);

  // Extended status color mapping for aircraft statuses (includes all statuses from AircraftStatusBoard)
  const aircraftStatusColors = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bbff',
    AWP: '#335d80ff',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d'
  };

  // Get available sites from parking spots
  const availableSites = useMemo(() => {
    const sites = [...new Set(parkingSpots.map(spot => spot.site).filter(Boolean))];
    return sites.sort();
  }, []);

  // Filter sites based on user groups
  const filteredSites = useMemo(() => {
    if (isStaff) {
      return availableSites;
    }
    // Filter to only show sites that match user's groups
    return availableSites.filter(site => groups.includes(site));
  }, [availableSites, groups, isStaff]);

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
    const deltaLon = ((lon2 - lon1) * Math.PI) / 180;
    
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) *
              Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  };

  // Find nearest parking spot for an aircraft (only within same site)
  const findNearestParkingSpot = useCallback((aircraft) => {
    // Use altitude to determine if on ground (< 100 feet / 30.48 meters)
    const altitude = aircraft.altitude || 0;
    if (altitude >= 30.48) return null; // Aircraft is in air
    
    // Use site from aircraft data if available, otherwise determine from coordinates
    const aircraftSite = aircraft.site;
    if (!aircraftSite) {
      console.log(`⚠ Aircraft ${aircraft.hex_id} at [${aircraft.latitude.toFixed(6)}, ${aircraft.longitude.toFixed(6)}] - unknown site location`);
      return null;
    }
    
    let nearestSpot = null;
    let minDistance = Infinity;
    const threshold = 10; // Increased to 10 meters to catch more aircraft
    
    // Only check spots at the same site as the aircraft
    parkingSpots.forEach(spot => {
      // Use site from parking spot data if available, otherwise determine from coordinates
      const spotSite = spot.site;
      
      // Skip spots that are not at the same site
      if (spotSite !== aircraftSite) {
        return;
      }
      
      const distance = calculateDistance(
        aircraft.latitude, 
        aircraft.longitude, 
        spot.lat, 
        spot.lon
      );
      
      // Debug logging for aircraft that might be near spots
      if (distance < 50) { // Log if within 50 meters for debugging
        console.log(`Aircraft ${aircraft.hex_id} (${aircraft.callsign || 'Unknown'}) at [${aircraft.latitude.toFixed(6)}, ${aircraft.longitude.toFixed(6)}] (${aircraftSite}) is ${distance.toFixed(2)}m from spot ${spot.spot} at [${spot.lat.toFixed(6)}, ${spot.lon.toFixed(6)}] (${spotSite})`);
      }
      
      // Consider aircraft "at" a parking spot if within threshold
      if (distance < threshold && distance < minDistance) {
        minDistance = distance;
        nearestSpot = spot;
      }
    });
    
    if (nearestSpot) {
      console.log(`✓ Nearest spot for aircraft ${aircraft.hex_id} (${aircraftSite}) is ${nearestSpot.spot} (${aircraftSite}) (distance: ${minDistance.toFixed(2)}m)`);
    } else {
      // Find the closest spot even if outside threshold for debugging (same site only)
      const altitude = aircraft.altitude || 0;
      if (altitude < 30.48) {
        let closestSpot = null;
        let closestDistance = Infinity;
        parkingSpots.forEach(spot => {
          const spotSite = spot.site;
          if (spotSite !== aircraftSite) return;
          
          const distance = calculateDistance(
            aircraft.latitude, 
            aircraft.longitude, 
            spot.lat, 
            spot.lon
          );
          if (distance < closestDistance) {
            closestDistance = distance;
            closestSpot = spot;
          }
        });
        if (closestSpot) {
          console.log(`⚠ Aircraft ${aircraft.hex_id} (${aircraftSite}) on ground but ${closestDistance.toFixed(2)}m from nearest spot ${closestSpot.spot} (${aircraftSite}) (threshold: ${threshold}m)`);
        }
      }
    }
    
    return nearestSpot;
  }, []);

  // Fetch existing assignments from backend - server-side handles all assignment logic
  const fetchSpotAssignments = useCallback(async () => {
    try {
      const response = await api.get('/aircraft-spot-assignments/?is_active=true');
      const assignments = response.data;
      
      console.log('Fetched assignments from backend:', assignments.length);
      
      // Convert to local state format: { hex_id: { spot, timestamp, aircraft } }
      // Simply display what the server provides - no client-side validation or modification
      const assignmentsMap = {};
      assignments.forEach(assignment => {
        assignmentsMap[assignment.hex_id] = {
          spot: assignment.parking_spot,
          timestamp: assignment.last_seen_at,
          site: assignment.site,
          aircraft: {
            hex_id: assignment.hex_id,
            callsign: assignment.callsign,
            buno: assignment.buno,
            latitude: assignment.latitude,
            longitude: assignment.longitude,
            track: assignment.track,
            site: assignment.site
          },
          buno: assignment.buno // Store BUNO for status lookup
        };
        console.log(`Loaded assignment: ${assignment.hex_id} -> ${assignment.parking_spot}`);
      });
      
      setSpotAssignments(assignmentsMap);
      return assignmentsMap;
    } catch (err) {
      console.error('Error fetching spot assignments:', err);
      return {};
    }
  }, []);

  // Fetch aircraft from ADSB Exchange via backend proxy (filters applied server-side)
  const fetchADSBExchangeAircraft = useCallback(async () => {
    try {
      return await fetchAdsbExchangeAircraft({ convertAltitudeToMeters: true });
    } catch (err) {
      console.error('Error fetching ADSBExchange aircraft:', err);
      return [];
    }
  }, []);

  // Fetch backend ADS-B aircraft data only
  const fetchBackendAircraft = useCallback(async () => {
    try {
      return await fetchBackendAdsbAircraft();
    } catch (err) {
      console.error('Error fetching backend aircraft:', err);
      return [];
    }
  }, []);
      
  // Merge aircraft data from both sources
  const mergeAircraftData = useCallback((backendAircraft, adsbExchangeAircraft) => {
      const aircraftMap = new Map();
      
      // Add backend aircraft first
      backendAircraft.forEach(aircraft => {
        if (aircraft.hex_id) {
          aircraft.source = 'backend'; // Mark as server-side API
          aircraftMap.set(aircraft.hex_id, aircraft);
        }
      });
      
      // Add ADSBExchange aircraft, but don't overwrite existing entries
      adsbExchangeAircraft.forEach(aircraft => {
        if (aircraft.hex_id && !aircraftMap.has(aircraft.hex_id)) {
          // Keep site from the feed when present; do not infer from GPS boxes.
          aircraft.source = 'adsbexchange'; // Mark as ADSBExchange
          aircraftMap.set(aircraft.hex_id, aircraft);
        }
      });
      
    return Array.from(aircraftMap.values());
  }, []);

  // Fetch ADS-B aircraft data - server-side handles all assignment logic
  const fetchADSBAircraft = useCallback(async () => {
    try {
      // Fetch from both sources independently - use allSettled so one failure doesn't block the other
      const [backendResult, adsbExchangeResult] = await Promise.allSettled([
        fetchBackendAircraft(),
        fetchADSBExchangeAircraft().catch(err => {
          console.warn('ADSBExchange API error:', err);
          return [];
        })
      ]);
        
      // Extract data from results, handling both success and failure cases
      const backendAircraft = backendResult.status === 'fulfilled' 
        ? backendResult.value
        : [];
      const adsbExchangeAircraft = adsbExchangeResult.status === 'fulfilled'
        ? adsbExchangeResult.value || []
        : [];
      
      // Merge aircraft data
      const mergedAircraft = mergeAircraftData(backendAircraft, adsbExchangeAircraft);
      
      console.log('Fetched aircraft data:', {
        backend: backendAircraft.length,
        adsbExchange: adsbExchangeAircraft.length,
        merged: mergedAircraft.length
      });
      
      setAdsbAircraft(mergedAircraft);
        
      return mergedAircraft;
    } catch (err) {
      console.error('Error fetching ADS-B aircraft data:', err);
      return [];
    }
  }, [fetchADSBExchangeAircraft, fetchBackendAircraft, mergeAircraftData]);

  // Fetch and update ADSB Exchange data independently for faster updates
  const fetchAndUpdateADSBExchange = useCallback(async () => {
    try {
      const adsbExchangeAircraft = await fetchADSBExchangeAircraft().catch(err => {
        console.warn('ADSBExchange API error:', err);
        return [];
      });
      
      // Update immediately by merging with current state (preserves backend aircraft)
      setAdsbAircraft(prevAircraft => {
        const currentBackend = prevAircraft.filter(a => a.source === 'backend');
        return mergeAircraftData(currentBackend, adsbExchangeAircraft);
      });
    } catch (err) {
      console.error('Error updating ADSB Exchange data:', err);
    }
  }, [fetchADSBExchangeAircraft, mergeAircraftData]);

  // Fetch aircraft status data
  const fetchAircraftStatus = useCallback(async () => {
    try {
      const response = await api.get('/aircraft-status/');
      let aircraftData = response.data;
      
      // Handle paginated response
      if (response.data.results) {
        aircraftData = response.data.results;
      }
      
      // Convert to map: { buno: status }
      const statusMap = {};
      if (Array.isArray(aircraftData)) {
        aircraftData.forEach(aircraft => {
          if (aircraft.buno) {
            statusMap[aircraft.buno] = aircraft.status || '';
          }
        });
      }
      
      setAircraftStatus(statusMap);
    } catch (err) {
      console.error('Error fetching aircraft status:', err);
    }
  }, []);

  // Fetch assignments and aircraft data on component mount
  useEffect(() => {
    const loadData = async () => {
      const filters = await fetchAdsbFilters();
      setAdsbPollInterval(filters.adsb_exchange_poll_interval_ms || 2000);

      await Promise.all([
        fetchADSBAircraft(),
        fetchAircraftStatus(),
        fetchSpotAssignments()
      ]);
    };
    
    loadData();
    
    const adsbExchangeInterval = adsbPollInterval;
    const backendUpdateInterval = selectedSite ? 1000 : 10000;
    
    // Fast polling for ADSB Exchange data
    const adsbExchangeTimer = setInterval(() => {
      fetchAndUpdateADSBExchange();
    }, adsbExchangeInterval);
    
    // Slower polling for backend data, status, and assignments
    const backendTimer = setInterval(() => {
      Promise.all([
        fetchBackendAircraft().then(backendAircraft => {
          // Merge with current ADSB Exchange data from state
          setAdsbAircraft(prevAircraft => {
            const currentADSBExchange = prevAircraft.filter(a => a.source === 'adsbexchange');
            return mergeAircraftData(backendAircraft, currentADSBExchange);
          });
        }),
        fetchAircraftStatus(),
        fetchSpotAssignments()
      ]);
    }, backendUpdateInterval);
    
    return () => {
      clearInterval(adsbExchangeTimer);
      clearInterval(backendTimer);
    };
  }, [selectedSite, adsbPollInterval, fetchSpotAssignments, fetchADSBAircraft, fetchAircraftStatus, fetchAndUpdateADSBExchange, fetchBackendAircraft, mergeAircraftData]);

  // Get color based on altitude (similar to tar1090)
  const getAltitudeColor = (altitudeMeters, isOnGround = false) => {
    // Black is only for ground (0ft or is_on_ground = true)
    if (isOnGround || altitudeMeters === 0) {
      return '#000000'; // Black - Ground only
    }
    
    const altitudeFeet = altitudeMeters * 3.28084; // Convert meters to feet
    
    // tar1090-style altitude-based coloring
    if (altitudeFeet < 1) {
      return '#000000'; // Black - Ground (safety check)
    } else if (altitudeFeet <= 100) {
      return '#D2B48C'; // Tan - Very low altitude (1-100ft)
    } else if (altitudeFeet < 1000) {
      return '#FFA500'; // Orange - Low altitude (100-1000ft)
    } else if (altitudeFeet < 10000) {
      return '#39B54A'; // Green - Medium altitude
    } else if (altitudeFeet < 25000) {
      return '#007bff'; // Blue - High altitude
    } else {
      return '#8B00FF'; // Purple - Very high altitude
    }
  };

  // Normalize aircraft heading/track with robust fallback order
  const getAircraftHeading = (aircraft) => {
    const candidates = [
      aircraft.track,
      aircraft.trk,
      aircraft.true_heading,
      aircraft.mag_heading,
      aircraft.nav_heading,
      aircraft.heading
    ];
    for (const value of candidates) {
      if (value !== null && value !== undefined && !isNaN(value)) {
        return Number(value);
      }
    }
    return 0;
  };

  // Create aircraft icon based on ADS-B data and source
  const createAircraftIcon = (aircraft) => {
    const isADSBExchange = aircraft.source === 'adsbexchange';
    
    // Get altitude in meters
    const altitude = aircraft.altitude || 0;
    
    // Use API's is_on_ground field if available, otherwise calculate from altitude
    let isOnGround;
    if (aircraft.is_on_ground !== null && aircraft.is_on_ground !== undefined) {
      isOnGround = Boolean(aircraft.is_on_ground);
    } else {
      // Fallback: Determine if aircraft is on ground based on altitude
      // Use altitude from API: typically < 100 feet (30.48 meters) is considered on ground
      isOnGround = altitude < 30.48; // 30.48 meters = 100 feet
    }
    
    // Get color based on altitude and ground status
    const color = getAltitudeColor(altitude, isOnGround);
    
    const size = isOnGround ? 35 : 40; // Larger size for better visibility
    
    // Get heading/track direction (0-360 degrees, where 0 is North)
    // Track is the direction of travel, heading is where the nose points
    const heading = getAircraftHeading(aircraft);
    // Convert to rotation: CSS rotate uses degrees, and we assume aircraft image points right (90°)
    // So we need to subtract 90 to align with North (0°)
    const rotation = heading - 90;
    
    if (isADSBExchange) {
      const outlineColor = (isOnGround || altitude === 0) ? '#ffffff' : '#000000';
      const half = Math.round(size * 0.28);
      return L.divIcon({
        className: 'custom-div-icon adsbexchange-aircraft',
        html: `<div style="
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: auto;
          cursor: pointer;
        ">
          <div
            aria-label="Aircraft"
            style="
              width: 0;
              height: 0;
              border-left: ${half}px solid transparent;
              border-right: ${half}px solid transparent;
              border-bottom: ${size}px solid ${color};
              filter: drop-shadow(1px 0 0 ${outlineColor}) drop-shadow(-1px 0 0 ${outlineColor}) drop-shadow(0 1px 0 ${outlineColor}) drop-shadow(0 -1px 0 ${outlineColor});
              transform: rotate(${heading}deg);
              transform-origin: center 70%;
              pointer-events: none;
            "
          ></div>
        </div>`,
        iconSize: [size + 2, size + 2],
        iconAnchor: [(size + 2) / 2, (size + 2) / 2]
      });
    } else {
      // Use standard icon for server-side API aircraft
      // Color is already determined by altitude in getAltitudeColor function above
      const iconSize = isOnGround ? 15 : 20;
      
      return L.divIcon({
        className: 'custom-div-icon backend-aircraft',
        html: `<div style="
          background-color: ${color};
          width: ${iconSize}px;
          height: ${iconSize}px;
          border-radius: 50%;
          border: 2px solid #000000;
          box-shadow: 0 0 0 1px #ffffff, 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          font-weight: bold;
          color: white;
          text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
          transform: rotate(${rotation}deg);
          transform-origin: center center;
        ">✈</div>`,
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize/2, iconSize/2]
      });
    }
  };


  // Create custom icon for parking spots
  const createStatusIcon = (spot) => {
    // Check if spot has an assigned aircraft
    const hasAssignedAircraft = spot.assignedAircraft;
    
    let color, displayText, borderColor, size;
    
    if (hasAssignedAircraft && spot.assignedStatus) {
      // Use aircraft status color if available
      color = aircraftStatusColors[spot.assignedStatus] || '#007bff';
      displayText = spot.assignedStatus;
      borderColor = 'white';
      size = 24;
    } else if (hasAssignedAircraft) {
      // Assigned but no status - use blue
      color = '#007bff';
      displayText = '✈';
      borderColor = '#0056b3';
      size = 24;
    } else {
      // Use spot's own status color if no aircraft assigned
      color = statusColors[spot.status] || '#e9ecef';
      displayText = spot.status || 'E';
      borderColor = 'white';
      size = 20;
    }
    
    return L.divIcon({
      className: 'custom-div-icon',
      html: `<div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 2px solid ${borderColor};
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${size === 24 ? '12px' : '10px'};
        font-weight: bold;
        color: white;
        text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
      ">${displayText}</div>`,
      iconSize: [size, size],
      iconAnchor: [size/2, size/2]
    });
  };

  // Calculate map center based on selected site, search result, or default to all spots
  // If there are aircraft outside known sites, use a wider view
  const mapCenter = useMemo(() => {
    // If searching and found an aircraft, center on it
    if (searchResult && searchResult.latitude && searchResult.longitude) {
      return [searchResult.latitude, searchResult.longitude];
    }
    
    if (selectedSite && SITE_CENTERS[selectedSite]) {
      return SITE_CENTERS[selectedSite];
    }
    
    // When no site is selected, show entire United States
    // Center of continental US: approximately [39.8283, -98.5795]
    return [39.8283, -98.5795];
  }, [selectedSite, searchResult]);

  // Map zoom level - lower zoom for wider view
  const mapZoom = useMemo(() => {
    // If searching and found an aircraft, zoom in on it
    if (searchResult && searchResult.latitude && searchResult.longitude) {
      return 16; // Zoomed in on found aircraft
    }
    
    if (selectedSite) {
      return 18; // Zoomed in for specific site
    }
    return 4; // Wide view for entire United States
  }, [selectedSite, searchResult]);

  // Filter spots by selected site
  const filteredParkingSpots = useMemo(() => {
    if (selectedSite) {
      return parkingSpots.filter(spot => spot.site === selectedSite);
    } else if (!isStaff) {
      // If no site selected and user is not staff, show only their accessible sites
      return parkingSpots.filter(spot => filteredSites.includes(spot.site));
    }
    return parkingSpots;
  }, [selectedSite, isStaff, filteredSites]);

  // Filter aircraft by selected site
  const filteredAircraft = useMemo(() => {
    // First filter out invalid aircraft (missing coordinates or hex_id)
    const validAircraft = adsbAircraft.filter(aircraft => {
      return aircraft && 
             aircraft.hex_id && 
             typeof aircraft.latitude === 'number' && 
             typeof aircraft.longitude === 'number' &&
             !isNaN(aircraft.latitude) && 
             !isNaN(aircraft.longitude) &&
             aircraft.latitude !== 0 && 
             aircraft.longitude !== 0;
    });
    
    console.log('Filtering aircraft:', {
      total: adsbAircraft.length,
      valid: validAircraft.length,
      selectedSite,
      isStaff
    });
    
    if (selectedSite) {
      // When a specific site is selected, only show aircraft at that site
      const filtered = validAircraft.filter(aircraft => {
        const aircraftSite = aircraft.site;
        return aircraftSite === selectedSite;
      });
      console.log(`Filtered by site ${selectedSite}:`, filtered.length);
      return filtered;
    }
    // When no site is selected, show ALL aircraft (entire United States view)
    console.log('No site selected - showing all valid aircraft:', validAircraft.length);
    return validAircraft;
  }, [adsbAircraft, selectedSite, isStaff]);

  // Memoize nearest spot calculations for all filtered aircraft to avoid recalculating during render
  const aircraftNearestSpots = useMemo(() => {
    const spotsMap = new Map();
    filteredAircraft.forEach(aircraft => {
      const nearestSpot = findNearestParkingSpot(aircraft);
      if (nearestSpot) {
        spotsMap.set(aircraft.hex_id, nearestSpot);
      }
    });
    return spotsMap;
  }, [filteredAircraft, findNearestParkingSpot]);

  // Search for aircraft by registration or BUNO
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResult(null);
      return;
    }

    const searchLower = searchTerm.trim().toLowerCase();
    const foundAircraft = filteredAircraft.find(aircraft => {
      const registration = String(aircraft.registration || aircraft.callsign || '').toLowerCase();
      const buno = String(aircraft.buno || '').toLowerCase();
      const hexId = String(aircraft.hex_id || '').toLowerCase();
      
      return registration.includes(searchLower) || 
             buno.includes(searchLower) || 
             hexId.includes(searchLower);
    });

    setSearchResult(foundAircraft || null);
  }, [searchTerm, filteredAircraft]);

  // Create merged spots array that includes assigned aircraft and status
  const mergedSpots = filteredParkingSpots.map(spot => {
    // Find if any aircraft is assigned to this spot at this site
    // Match by both spot name AND site (spot names are not unique across sites)
    const spotSite = spot.site;
    const assignment = Object.values(spotAssignments).find(assign => {
      if (assign.spot !== spot.spot) return false;
      const assignSite = assign.site || (assign.aircraft ? assign.aircraft.site : null);
      return assignSite === spotSite;
    });
    
    if (assignment && assignment.aircraft) {
      // Get aircraft status from BUNO
      const buno = assignment.buno || assignment.aircraft.buno;
      const aircraftStatusValue = buno ? (aircraftStatus[buno] || '') : '';
      
      // Merge spot with assigned aircraft data
      return {
        ...spot,
        hex: assignment.aircraft.hex_id || spot.hex,
        buno: spot.buno || '', // Keep existing buno if present
        assignedAircraft: assignment.aircraft,
        assignedTimestamp: assignment.timestamp,
        assignedBuno: buno,
        assignedStatus: aircraftStatusValue
      };
    }
    
    return spot;
  });

  // Use all merged spots (no status filtering)
  const filteredSpots = mergedSpots;

  return (
    <div className="adsb-aircraft-map" style={{ height: '100%', width: '100%' }}>
      {/* Site Filter and Search */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
      }}>
        {/* Search Box */}
        <div className="aircraft-search" style={{
          background: theme === 'dark' ? '#2d3748' : 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
        }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Search Aircraft:
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Registration or BUNO"
            style={{
              padding: '5px 10px',
              borderRadius: '4px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#4a5568' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              width: '150px',
              boxSizing: 'border-box'
            }}
          />
          {searchTerm && (
            <div style={{
              marginTop: '5px',
              fontSize: '12px',
              color: searchResult 
                ? (theme === 'dark' ? '#28a745' : '#155724')
                : (theme === 'dark' ? '#dc3545' : '#721c24')
            }}>
              {searchResult 
                ? `✓ Found: ${searchResult.buno || searchResult.registration || searchResult.callsign || 'Unknown'}`
                : '✗ Aircraft not found'
              }
            </div>
          )}
        </div>

        {/* Map Layers */}
        <div className="map-layers" style={{
          background: theme === 'dark' ? '#2d3748' : 'white',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`,
          minWidth: '150px'
        }}>
          <div style={{
            marginBottom: '8px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
            fontSize: '14px'
          }}>
            Map Layers
          </div>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '6px',
            fontSize: '13px',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showOpenAip}
              onChange={(e) => setShowOpenAip(e.target.checked)}
            />
            openAIP
          </label>
          {showOpenAip && !openAipApiKey && (
            <div style={{
              marginBottom: '6px',
              fontSize: '11px',
              color: theme === 'dark' ? '#f6ad55' : '#c05621',
              lineHeight: 1.3
            }}>
              Set REACT_APP_OPENAIP_API_KEY to enable tiles
            </div>
          )}
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showNoaaRadar}
              onChange={(e) => setShowNoaaRadar(e.target.checked)}
            />
            NOAA Radar
          </label>
        </div>
      </div>

      {/* Legend */}
      <div className="status-legend" style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        zIndex: 1000,
        background: theme === 'dark' ? '#2d3748' : 'white',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        border: `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`,
        maxWidth: '300px'
      }}>
        <h4 style={{
          margin: '0 0 10px 0',
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
          fontSize: '16px'
        }}>
          Aircraft Altitude
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#000000',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Ground (0ft)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#D2B48C',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Very Low (1-100ft)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#FFA500',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Low (100-1000ft)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#39B54A',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Medium (1K-10K ft)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#007bff',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                High (10K-25K ft)
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#8B00FF',
                borderRadius: '50%',
                border: '1px solid white',
                boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}></div>
              <span style={{
                fontSize: '12px',
                color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
              }}>
                Very High (25K+ ft)
              </span>
            </div>
          </div>
      </div>

      {/* Map */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <ChangeView center={mapCenter} zoom={mapZoom} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {showOpenAip && openAipApiKey && (
          <TileLayer
            attribution='&copy; <a href="https://www.openaip.net">openAIP</a>'
            url={`https://{s}.api.tiles.openaip.net/api/data/openaip/{z}/{x}/{y}.png?apiKey=${openAipApiKey}`}
            subdomains="abc"
            opacity={0.7}
            maxNativeZoom={16}
            zIndex={200}
          />
        )}

        {showNoaaRadar && (
          <TileLayer
            key={`noaa-radar-${radarCacheBust}`}
            attribution='Radar &copy; <a href="https://www.weather.gov/">NWS NEXRAD</a> via <a href="https://mesonet.agron.iastate.edu/">IEM</a>'
            url={`https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png?${radarCacheBust}`}
            opacity={0.55}
            zIndex={300}
          />
        )}
        
        {/* 75-mile radius circles for each site center */}
        {Object.entries(SITE_CENTERS).map(([siteName, center]) => {
          const radiusMeters = 75 * 1609.34; // Convert 75 miles to meters (1 mile = 1609.34 meters)
          // Calculate a point on the circle's edge (at 90 degrees = east)
          // metersPerDegreeLat ≈ 111320, metersPerDegreeLon ≈ 111320 * cos(lat)
          const metersPerDegreeLat = 111320;
          const metersPerDegreeLon = 111320 * Math.cos(center[0] * Math.PI / 180);
          const angleRad = 90 * Math.PI / 180; // 90 degrees = east
          const labelLat = center[0] + (radiusMeters / metersPerDegreeLat) * Math.cos(angleRad);
          const labelLon = center[1] + (radiusMeters / metersPerDegreeLon) * Math.sin(angleRad);
          
          return (
            <React.Fragment key={siteName}>
              <Circle
                center={center}
                radius={radiusMeters}
                pathOptions={{
                  color: 'black',
                  fillColor: 'transparent',
                  fillOpacity: 0,
                  weight: 2
                }}
              />
              <Marker
                position={[labelLat, labelLon]}
                icon={L.divIcon({
                  className: 'radius-label-icon',
                  html: `<div style="
                    color: white;
                    font-size: 12px;
                    font-weight: bold;
                    text-align: center;
                    white-space: nowrap;
                    text-shadow: 
                      -1px -1px 0 #000,
                      1px -1px 0 #000,
                      -1px 1px 0 #000,
                      1px 1px 0 #000,
                      0 0 2px #000;
                    pointer-events: none;
                  ">75 miles radius</div>`,
                  iconSize: [120, 20],
                  iconAnchor: [60, 10]
                })}
              />
            </React.Fragment>
          );
        })}
        
        {/* Parking Spots */}
        {filteredSpots.map((spot) => {
          const spotSite = spot.site;
          const uniqueKey = spotSite ? `${spotSite}_${spot.spot}` : spot.spot;
          return (
            <Marker
              key={uniqueKey}
              position={[spot.lat, spot.lon]}
            icon={createStatusIcon(spot)}
          >
            <Popup>
              <div style={{ minWidth: '200px' }}>
                <h3 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>
                  Spot: {spot.spot}
                </h3>

                {spot.assignedAircraft && (
                  <div style={{ 
                    marginBottom: '8px', 
                    padding: '8px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    border: '1px solid #90caf9'
                  }}>
                    <strong style={{ color: '#1976d2' }}>Assigned Aircraft</strong>
                    {spot.assignedAircraft.buno && (
                      <div style={{ marginTop: '4px' }}>
                        <strong>BUNO:</strong> {spot.assignedAircraft.buno}
                      </div>
                    )}
                    <div>
                      <strong>Status:</strong> {spot.assignedStatus || spot.status || 'Empty'}
                    </div>
                    <div>
                      <strong>Hex ID:</strong> {spot.assignedAircraft.hex_id}
                    </div>
                    {spot.assignedTimestamp && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                        Last seen: {new Date(spot.assignedTimestamp).toLocaleString(navigator.language, { dateStyle: 'short', timeStyle: 'short' })}
                      </div>
                    )}
                  </div>
                )}
                {spot.buno && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>BUNO:</strong> {spot.buno}
                  </div>
                )}
                {spot.hex && !spot.assignedAircraft && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Hex ID:</strong> {spot.hex}
                  </div>
                )}

                {spot.status && statusDescriptions[spot.status] && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontStyle: 'italic',
                    marginTop: '8px',
                    padding: '5px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px'
                  }}>
                    {statusDescriptions[spot.status]}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
          );
        })}

        {/* ADS-B Aircraft */}
        {filteredAircraft.length > 0 && (
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 2000,
            background: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '12px',
            display: 'none' // Hidden by default, can be enabled for debugging
          }}>
            Rendering {filteredAircraft.length} aircraft
          </div>
        )}
        {filteredAircraft.map((aircraft) => {
          // Validate coordinates before rendering
          if (!aircraft.latitude || !aircraft.longitude || 
              isNaN(aircraft.latitude) || isNaN(aircraft.longitude)) {
            console.warn('Invalid aircraft coordinates:', aircraft);
            return null;
          }
          
          // Use memoized nearest spot instead of calculating during render
          const nearestSpot = aircraftNearestSpots.get(aircraft.hex_id);
          return (
            <Marker
              key={aircraft.hex_id}
              position={[aircraft.latitude, aircraft.longitude]}
              icon={createAircraftIcon(aircraft)}
              eventHandlers={{
                click: () => {
                  // Marker is clickable, popup will open automatically
                }
              }}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h3 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>
                    {aircraft.callsign || aircraft.buno || 'Unknown'}
                  </h3>
                  
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Registration:</strong> {aircraft.buno || aircraft.registration || 'N/A'}
                  </div>
                  
                  {aircraft.site && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Site:</strong> {aircraft.site}
                    </div>
                  )}
                  
                  {aircraft.altitude !== null && aircraft.altitude !== undefined && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Altitude:</strong> {aircraft.altitude.toFixed(0)}m ({(aircraft.altitude * 3.28084).toFixed(0)}ft)
                    </div>
                  )}
                  
                  {aircraft.ground_speed !== null && aircraft.ground_speed !== undefined && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Speed:</strong> {aircraft.ground_speed.toFixed(0)} knots
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Hex ID:</strong> {aircraft.hex_id}
                  </div>
                  
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Status:</strong> {
                      (aircraft.is_on_ground !== null && aircraft.is_on_ground !== undefined) 
                        ? (aircraft.is_on_ground ? 'On Ground' : 'In Air')
                        : ((aircraft.altitude || 0) < 30.48 ? 'On Ground' : 'In Air')
                    }
                  </div>
                  
                  {aircraft.track !== null && aircraft.track !== undefined && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Track:</strong> {aircraft.track.toFixed(1)}°
                    </div>
                  )}
                  
                  {nearestSpot && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Nearest Spot:</strong> {nearestSpot.spot}
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Coordinates:</strong><br />
                    Lat: {aircraft.latitude.toFixed(6)}<br />
                    Lon: {aircraft.longitude.toFixed(6)}
                  </div>
                  
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontStyle: 'italic',
                    marginTop: '8px',
                    padding: '5px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px'
                  }}>
                    Last updated: {new Date(aircraft.timestamp).toLocaleString(navigator.language, { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

    </div>
  );
};

export default ADSBAircraftMap;
